import { Box, Button } from '@mui/material';
import { Helmet } from 'react-helmet-async';
import { useDateRangeOptionSelector } from 'components/DateRangeSelector/useDateRangeOptionSelector';
import { MouseEventHandler, useCallback, useEffect } from 'react';
import { InactiveTeamsTable } from './InactiveTeamsTable';
import useLocalDispatch from 'hooks/useLocalDispatch';
import useTeams from 'hooks/useTeams';
import { fetchActiveTeams } from 'thunk';
import { FetchActiveTeamsSuccessResponse } from 'ReportsModule/types';
import { DateRangeOptions } from '@constants';
import { DateRangeOptionSelectorUI } from 'components/DateRangeSelector/DateRangeOptionSelectorUI';
import StandardPageHeader from 'components/StandardPageHeader';

const stateId = 'fetchActiveTeams';

export const InactiveTeamsContainer = () => {
  const { openDateRangeMenu, selectedDateRangeOption, onCustomDateChange, dates } =
    useDateRangeOptionSelector();

  const { dispatchLocal, selectLocalData } = useLocalDispatch();

  const triggerInitialFetch = useCallback(
    ({ lastActiveDate }: { lastActiveDate: string }) => {
      dispatchLocal(fetchActiveTeams, {
        params: {
          date_filter: lastActiveDate,
          give_inactive_teams: true,
        },
        shouldUseMeta: true,
        formatSuccessResponse({ response }) {
          const results = Object.keys(response.data.stats_res).map(Number);
          return results;
        },
        stateId,
      });
    },
    [dispatchLocal],
  );

  useEffect(() => {
    if (selectedDateRangeOption.value !== DateRangeOptions.Custom) {
      triggerInitialFetch({ lastActiveDate: dates.startDate });
    }
  }, [dates.startDate, selectedDateRangeOption.value, triggerInitialFetch]);

  const onDateRangeMenuClick: MouseEventHandler<HTMLElement> = (e) => {
    openDateRangeMenu({
      target: e.currentTarget,
    });
  };

  const { data, isRequesting, error } = selectLocalData<number[] | undefined>({
    stateId,
  });

  return (
    <>
      <Helmet title="Inactive Teams" />

      <StandardPageHeader
        title="Inactive Teams"
        subtitle="Monitor and manage teams with low activity"
        icon="Reports"
        sx={{ mb: 6 }}
      />

      <Box
        sx={{
          display: 'flex',
          justifyContent: 'flex-end',
          alignItems: 'center',
          gap: 10,
        }}
      >
        <DateRangeOptionSelectorUI
          onClick={onDateRangeMenuClick}
          selectorLabel={selectedDateRangeOption.label}
          dateRangeOption={selectedDateRangeOption.value}
          onCustomDateChange={onCustomDateChange}
          startDateLabel={'Since Date'}
        />
        {selectedDateRangeOption.value === DateRangeOptions.Custom ? (
          <Button
            color={'primary'}
            variant={'contained'}
            onClick={() =>
              triggerInitialFetch({
                lastActiveDate: dates.startDate,
              })
            }
          >
            Search
          </Button>
        ) : (
          <></>
        )}
      </Box>

      <InactiveTeamsTable inactiveTeamIds={data || []} isLoading={isRequesting} />
    </>
  );
};
